"use client";

import dynamic from "next/dynamic";
import type { KundaliLang } from "./KundaliFormCard.i18n";

const DailyInsight = dynamic(() => import("@/components/DailyInsight"), { ssr: false });

export function FormErrorMessage({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      className="text-[13px] rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

export function KundaliSubmitButton({
  loading,
  label,
  loadingLabel,
}: {
  loading: boolean;
  label: string;
  loadingLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="btn-sacred w-full font-semibold py-3.5 rounded-lg text-[14px]"
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2.5">
          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          {loadingLabel}
        </span>
      ) : (
        label
      )}
    </button>
  );
}

// DailyInsight rendered height ≈ 340px (py-6 outer + rounded-2xl card p-8
// with text content + ShareActions + PersonalizeInsight + CategoryLabel).
// Without a reserved container the slot injects ~340px of content 5 s after
// initial paint, shifting AIAstrologerCTA + SEOContent (large) down by that
// amount → field p75 CLS ≈ 1.0 on /free-kundali/ (the only hard-failing Core
// Web Vital on the site). Reserving the same floor from t=0 eliminates the
// shift: content loads into a pre-reserved space, nothing moves.
// Over-reserving by a few pixels = harmless whitespace; under-reserving = residual CLS.
const DAILY_INSIGHT_MIN_HEIGHT = 340;

export function DailyInsightSlot({
  show,
  ready,
  language,
}: {
  show: boolean;
  ready: boolean;
  language: KundaliLang;
}) {
  if (!show) return null;
  return (
    <>
      <div className="divider-warm mx-auto max-w-sm mb-2" />
      <div style={{ minHeight: DAILY_INSIGHT_MIN_HEIGHT }}>
        {ready && <DailyInsight language={language} />}
      </div>
      <div className="divider-warm mx-auto max-w-sm mt-2 mb-6" />
    </>
  );
}
